/* Sprint C+D — Controlled External Competitor Discovery Panel.
 *
 * Calls POST /api/research-intel/projects/{id}/competitors/discover-external
 * and renders the discovery summary + budget remaining. Every result
 * is shown with "PENDING — needs your approval" badge. NEVER claims
 * the competitor has been analyzed or trusted.
 *
 * No new upload UX, no scraping, no LLM call.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Search, Globe2, ShieldOff, RefreshCw, ExternalLink, AlertTriangle,
} from "lucide-react";

export default function CompetitorDiscoveryExternalPanel({ projectId }) {
    const [budget, setBudget] = useState(null);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [override, setOverride] = useState("");

    const loadBudget = useCallback(async () => {
        if (!projectId) return;
        try {
            const { data } = await api.get(
                `/research-intel/projects/${projectId}/external-discovery-budget`,
            );
            setBudget(data);
        } catch (e) {
            // Silent — budget call is best-effort.
        }
    }, [projectId]);

    useEffect(() => { loadBudget(); }, [loadBudget]);

    const run = useCallback(async () => {
        if (!projectId) return;
        setRunning(true);
        try {
            const queries = override
                .split("\n")
                .map((q) => q.trim())
                .filter(Boolean)
                .slice(0, 3);
            const body = queries.length ? { queries } : {};
            const { data } = await api.post(
                `/research-intel/projects/${projectId}/competitors/discover-external`,
                body,
            );
            setResult(data);
            const n = (data?.inserted_competitors || []).length;
            if (n) {
                toast.success(
                    `Discovered ${n} unconfirmed competitor candidate${n === 1 ? "" : "s"}. Approve via Evidence panel.`,
                );
            } else {
                toast.info(data?.note || "No new candidates discovered.");
            }
            loadBudget();
        } catch (e) {
            toast.error(
                e?.userMessage
                || e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || "External discovery failed.",
            );
        } finally {
            setRunning(false);
        }
    }, [projectId, override, loadBudget]);

    const providerActive = !!budget?.provider_active;

    return (
        <div
            className="mt-4 card-base p-4 space-y-3"
            data-testid="competitor-discovery-external-panel"
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
                    <Globe2 className="w-4 h-4 text-violet-300" />
                    Discover Competitors from Web (Sprint C)
                </div>
                <div
                    className="text-[11px] inline-flex items-center gap-1 text-zinc-400"
                    data-testid="external-discovery-budget"
                >
                    {budget ? (
                        <>
                            <span className={providerActive ? "text-emerald-300" : "text-amber-300"}>
                                {providerActive ? "Provider live" : "Provider OFF"}
                            </span>
                            <span>•</span>
                            <span>
                                {budget.remaining}/{budget.budget} queries left today
                            </span>
                        </>
                    ) : (
                        <span>Loading budget…</span>
                    )}
                </div>
            </div>

            {!providerActive && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div>
                        External search provider is not configured. Set <code>EXTERNAL_SEARCH_PROVIDER</code> + <code>BRAVE_SEARCH_API_KEY</code> server-side to enable. You can still run the button — it will safely return zero results and spend zero budget.
                    </div>
                </div>
            )}

            <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wide text-zinc-500">
                    Optional — override queries (max 3, one per line)
                </label>
                <textarea
                    rows={2}
                    value={override}
                    onChange={(e) => setOverride(e.target.value)}
                    placeholder="leave empty to auto-build from industry / city / products"
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-2 text-xs text-zinc-200 placeholder:text-zinc-600"
                    data-testid="external-discovery-override-queries"
                />
                <div className="text-[10px] text-zinc-500">
                    Furmais only uses public-safe fields (industry, products, city, competitor seeds).
                    Your private brief / intake notes are never sent to the search provider.
                </div>
            </div>

            <button
                onClick={run}
                disabled={running}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs"
                data-testid="run-external-discovery-btn"
            >
                {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                {running ? "Discovering…" : "Run Controlled Discovery"}
            </button>

            {result && (
                <div className="space-y-2 pt-2 border-t border-zinc-800" data-testid="external-discovery-result">
                    <div className="text-[11px] text-zinc-400">
                        {result.note}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                        <Pill label="Queries used" value={(result.queries_used || []).length} />
                        <Pill label="Inserted" value={(result.inserted_competitors || []).length} />
                        <Pill label="Cache hits" value={result.cache_hits || 0} />
                        <Pill label="Skipped existing" value={result.skipped_existing || 0} />
                    </div>
                    {(result.inserted_competitors || []).length > 0 && (
                        <div className="space-y-1.5" data-testid="external-discovery-list">
                            {result.inserted_competitors.map((c) => (
                                <div
                                    key={c.id}
                                    className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950/40 px-2.5 py-1.5 text-xs"
                                >
                                    <div className="min-w-0">
                                        <div className="text-zinc-200 truncate">{c.name}</div>
                                        <div className="text-[10px] text-zinc-500 truncate">
                                            {c.website_url}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <span className="px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 text-[10px] inline-flex items-center gap-1">
                                            <ShieldOff className="w-3 h-3" /> Unconfirmed
                                        </span>
                                        <a
                                            href={c.website_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-zinc-400 hover:text-zinc-200"
                                            data-testid={`external-discovery-link-${c.id}`}
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Pill({ label, value }) {
    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/40 px-2 py-1.5 text-[11px]">
            <div className="text-zinc-500 uppercase tracking-wide text-[10px]">{label}</div>
            <div className="text-zinc-100 text-base font-semibold tabular-nums">{value}</div>
        </div>
    );
}
